import {
  Action,
  ActionFromReducersMapObject,
  CombinedState,
  combineReducers,
  configureStore,
  Reducer,
  StateFromReducersMapObject,
  ThunkAction,
} from '@reduxjs/toolkit';
import * as SentryReact from '@sentry/react';
import deepmerge, {Options} from 'deepmerge';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import {createWrapper, HYDRATE} from 'next-redux-wrapper';
import createSagaMiddleware from 'redux-saga';
import {all} from 'redux-saga/effects';
import superjson from 'superjson';
import {developerSlice} from '../common/DeveloperSlice';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {configSlice} from '../config/configSlice';
import {selectSlice} from '../domain/auth/id/verify/select/state/selectSlice';
import {ticketSlice} from '../domain/buy/ticket/state/ticketSlice';
import {loginSlice} from '../domain/common/state/loginSlice';
import {contentHomeSlice} from '../domain/content/[seriesId]/state/contentHomeSlice';
import {imageViewerSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/imageViewerSlice';
import {talkViewerSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/talkViewerSlice';
import {textViewerSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/textViewerSlice';
import {videoViewerSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/videoViewerSlice';
import {viewerEndSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/viewerEndSlice';
import {viewerInfoSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {viewerMenuSlice} from '../domain/content/[seriesId]/viewer/[productId]/state/viewerMenuSlice';
import talkViewerEventSaga from '../domain/content/[seriesId]/viewer/[productId]/util/talkViewerEventSaga';
import talkViewerSaga from '../domain/content/[seriesId]/viewer/[productId]/util/talkViewerSaga';
import textViewerEventSaga from '../domain/content/[seriesId]/viewer/[productId]/util/textViewerEventSaga';
import {helpSlice} from '../domain/help/state/helpSlice';
import {menuSlice} from '../domain/menu/util/MenuSlice';
import {myNewsSlice} from '../domain/mynews/util/myNewsSlice';
import {noticeSlice} from '../domain/notice/state/noticeSlice';
import {searchSlice} from '../domain/search/state/searchSlice';
import {signupSlice} from '../domain/signup/state/signupSlice';
import {todayGiftSlice} from '../domain/today-gift/state/todayGiftSlice';
import {errorsSlice} from '../errors/ErrorSlice';
import {footerSlice} from '../footer/FooterSlice';
import {headerSlice} from '../header/HeaderSlice';
import {pagewebLayoutSlice} from '../pagewebLayout/PagewebLayoutSlice';
import {GroupEntity, ItemEntity, SectionEntity} from '../pagewebLayout/types';
import {profileMenuSlice} from '../profileMenu/profileMenuSlice';
import {userSlice} from '../user/UserSlice';
import {listenerMiddleware} from './listenerMiddleware';

const reducer = {
  [menuSlice.name]: menuSlice.reducer,
  [noticeSlice.name]: noticeSlice.reducer,
  [helpSlice.name]: helpSlice.reducer,
  [pagewebLayoutSlice.name]: pagewebLayoutSlice.reducer,
  [contentHomeSlice.name]: contentHomeSlice.reducer,
  [ticketSlice.name]: ticketSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [footerSlice.name]: footerSlice.reducer,
  [errorsSlice.name]: errorsSlice.reducer,
  [searchSlice.name]: searchSlice.reducer,
  [myNewsSlice.name]: myNewsSlice.reducer,
  [profileMenuSlice.name]: profileMenuSlice.reducer,
  [todayGiftSlice.name]: todayGiftSlice.reducer,
  [headerSlice.name]: headerSlice.reducer,
  [viewerEndSlice.name]: viewerEndSlice.reducer,
  [talkViewerSlice.name]: talkViewerSlice.reducer,
  [viewerInfoSlice.name]: viewerInfoSlice.reducer,
  [videoViewerSlice.name]: videoViewerSlice.reducer,
  [viewerMenuSlice.name]: viewerMenuSlice.reducer,
  [imageViewerSlice.name]: imageViewerSlice.reducer,
  [textViewerSlice.name]: textViewerSlice.reducer,
  [configSlice.name]: configSlice.reducer,
  [developerSlice.name]: developerSlice.reducer,
  [signupSlice.name]: signupSlice.reducer,
  [loginSlice.name]: loginSlice.reducer,
  [selectSlice.name]: selectSlice.reducer,
};

type SliceNameToReducerMap = typeof reducer;
export type AppState = StateFromReducersMapObject<SliceNameToReducerMap>;
type AppAction = ActionFromReducersMapObject<SliceNameToReducerMap>;

export const rootReducer: Reducer<CombinedState<AppState>, AppAction> = (
  state,
  action
) => {
  switch (action.type) {
    case HYDRATE: {
      /**
       * CSR에서 page가 getInitialProps로 구성된 경우에 해당 페이지 내에서 리덕스 상태 업데이트가 진행되므로
       * 현재 상태를 리턴하고 종료합니다.
       */
      if (IS_CLIENT_SIDE && !action.payload) {
        return state as CombinedState<AppState>;
      }
      /**
       * server 에서 만든 initial state 값에 항상 클라이언트의 state 를 deepmerge 합니다.
       * 가장 명확한 방법은 client-server state 를 분리하는 것입니다(참고: https://github.com/kirill-konshin/next-redux-wrapper#server-and-client-state-separation).
       * 하지만 일정이 빠듯한 지금 시점에 분리하는건 어려워보입니다
       * 그래서 deepmerge 를 이용해 server 와 client 상태의 변경을 해결해봅니다
       * 1. SSR, SSG 에서 초기 상태를 만들게 됩니다. 이 값은 '모든 slice 의 state 가 INITIAL 로 되어있고, 현재 페이지만 새로운 데이터가 만들어진 상태' 입니다
       * 2. hydrate 시점에 __NEXT_REDUX_WRAPPER_HYDRATE__ 액션이 dispatch 됩니다.
       * 3. client-side 의 reducer 에 이 액션이 들어옵니다. 이 액션의 payload 에는 1번에서 만든 전체 state 가 담겨있습니다(현재 페이지 서버사이드에서 만든 상태가 있고 나머지는 INITIAL_STATE)
       * 4. server 에서 만든 상태를 먼저 넣고, deepmerge 를 통해 client-side 의 state 를 넣습니다.
       * 5. 즉, 어떤 경우에도 client-side 에서 만든 state 로 덮어쓰기 때문에 client-side 값이 날아갈 일은 없습니다
       * 6. 조금 우려되는 것은 server-side 에서 '데이터'를 넣는 것이 아니라 '특정 플래그를 변경하는 경우'가 있을때도 client-side 에서 덮어쓸 것 같은데.. 이런 케이스가 지금 존재하는지는 떠오르지 않네요;
       */
      // @ts-ignore
      const mergedState = deepmerge(action.payload, state, {
        arrayMerge: serverFirstArrayMerge,
      });

      return {
        ...mergedState,
        ...{
          // 서버사이드에서 불러와 변경하는 값은 hydrate 시 서버 데이터를 우선합니다(SSR, SSG 에서 메뉴 선택 등)
          menu: {
            ...mergedState.menu,
            entities: action.payload.menu.entities,
            relations: action.payload.menu.relations,
            selectedGnbMenuId: action.payload.menu.selectedGnbMenuId,
          },
          pagewebLayout: {
            ...mergedState?.pagewebLayout,
            entities: {
              ...mergedState?.pagewebLayout.entities,
              // 섹션 내의 컨트롤은 모두 서버사이드에서 이뤄지고 있기 때문에 서버 데이터를 우선합니다
              sections: deepmerge(
                action.payload.pagewebLayout.entities.sections,
                state?.pagewebLayout?.entities?.sections || {},
                {
                  arrayMerge: serverFirstArrayMerge,
                  customMerge: sectionCustomMerge,
                }
              ),
              items: deepmerge(
                action.payload.pagewebLayout.entities.items,
                state?.pagewebLayout?.entities?.items || {},
                {
                  customMerge: itemCustomMerge,
                }
              ),
            },
            relations: {
              ...mergedState?.pagewebLayout.relations,
              groups: deepmerge(
                action.payload.pagewebLayout.relations.groups,
                state?.pagewebLayout?.relations?.groups || {},
                {
                  arrayMerge: serverFirstArrayMerge,
                  customMerge: groupCustomMerge,
                }
              ),
            },
          },
        },
      };
    }
    default: {
      const combinedReducer = combineReducers(reducer);
      return combinedReducer(state, action);
    }
  }
};

const serverFirstArrayMerge: Options['arrayMerge'] = (target, source) => {
  // target(serverSide array) 없으면 source(clientSide array) 로 초기화해준다. 만약 serverSide array 가 있으면 ServerSide array를 우선시하여 그대로 사용한다
  return target.length === 0 ? source : target;
};

const itemCustomMerge: Options['customMerge'] = () => {
  return (serverSideItem: ItemEntity, clientSideItem: ItemEntity) => {
    const eventLog =
      'eventLog' in serverSideItem ? serverSideItem.eventLog : undefined;
    return {
      ...clientSideItem,
      ...(eventLog && {eventLog}),
    };
  };
};

const sectionCustomMerge: Options['customMerge'] = () => {
  return (
    serverSideSection: SectionEntity,
    clientSideSection: SectionEntity
  ) => {
    const totalCount: boolean | undefined =
      'totalCount' in serverSideSection
        ? (serverSideSection as {totalCount: boolean}).totalCount
        : undefined;
    const isEnd: boolean | undefined =
      'isEnd' in serverSideSection
        ? (serverSideSection as {isEnd: boolean}).isEnd
        : undefined;
    if ('param' in serverSideSection) {
      const parameterizedServerSideSection =
        serverSideSection as SectionEntity & {param: any};
      const parameterizedClientSideSection =
        clientSideSection as SectionEntity & {param: any};
      return {
        ...parameterizedClientSideSection,
        ...(totalCount !== undefined && {totalCount}),
        ...(isEnd !== undefined && {isEnd}),
        param: {
          ...parameterizedServerSideSection.param,
          // 섹션마다 url 기준으로 데이터가 동일한데 page 하나만 하단 스크롤 시 동적으로 변경된다. page 를 제외한 값이 동일하면 클라이언트에 캐싱된 데이터를 사용한다
          ...('page' in parameterizedClientSideSection.param && {
            page: isEqual(
              omit(parameterizedClientSideSection.param, 'page'),
              omit(parameterizedServerSideSection.param, 'page')
            )
              ? parameterizedClientSideSection.param.page
              : parameterizedServerSideSection.param.page,
          }),
        },
      };
    }
    return {
      ...clientSideSection,
      ...(totalCount !== undefined && {totalCount}),
      ...(isEnd !== undefined && {isEnd}),
    };
  };
};

/**
 * 본래 위 deepMerge 가 클라이언트 기준으로 머지되지만 이상하게 pagewebLayout 의 relation.groups 는 서버 기준으로 머지되고 있다. arrayMerge 조건에 걸리지 않은건가 싶은데 일단 무조건 클라 기준으로 변경한다.
 */
const groupCustomMerge: Options['customMerge'] = () => {
  return (serverSideGroup: GroupEntity, clientSideGroup: GroupEntity) => {
    return clientSideGroup;
  };
};
const sagaMiddleware = createSagaMiddleware();

function* rootSaga() {
  yield all([talkViewerSaga(), talkViewerEventSaga(), textViewerEventSaga()]);
}

const sentryReduxEnhancer = SentryReact.createReduxEnhancer();

const makeStore = () => {
  const enhancers = [sentryReduxEnhancer];
  const DevTools =
    IS_CLIENT_SIDE && process.env.NODE_ENV !== 'production'
      ? require('./DevTools').default
      : undefined;

  // CSR 환경이며 redux-devtools 플러그인을 사용하지 않는 개발 환경에서만 활성화합니다.
  if (DevTools && !window.__REDUX_DEVTOOLS_EXTENSION__) {
    const instrument = DevTools.instrument();
    enhancers.push(instrument);
  }

  const store = configureStore({
    reducer: rootReducer,
    devTools: process.env.NODE_ENV !== 'production',
    middleware: getDefaultMiddleware =>
      IS_CLIENT_SIDE
        ? getDefaultMiddleware()
            .concat(sagaMiddleware)
            .prepend(listenerMiddleware.middleware)
        : getDefaultMiddleware(), // *중요* 넥스트에서는 서버사이드에서 비동기로 redux action 을 대기하는 리스너(redux-saga, rtk listener)를 사용하지 않습니다. 비동기 리스너는 반드시 클라이언트사이드에서만 추가해야합니다. (memory leak)
    enhancers,
  });

  IS_CLIENT_SIDE && sagaMiddleware.run(rootSaga);
  return store;
};

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  Action
>;

export const nextReduxWrapper = createWrapper<AppStore>(makeStore, {
  serializeState: state => superjson.serialize(state),
  deserializeState: state => superjson.deserialize(state),
});
